import React, { forwardRef, useImperativeHandle, useMemo } from "react";
import { Typography, Table, Spin, Col } from "antd";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { resolveGaMeetingDataProp } from "utils/dpatAssessmentStartData";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";

const { Title } = Typography;

const GA_DPAT_SLUG = "c1.0-1.1";

const MEETING_DETAILS_COLUMNS = [
  { title: "Meeting", dataIndex: "meeting", key: "meeting" },
  { title: "Invitation Date", dataIndex: "invitationDate", key: "invitationDate" },
  { title: "Meeting Date", dataIndex: "meetingDate", key: "meetingDate" },
  { title: "Interval (Days)", dataIndex: "interval", key: "interval" },
  { title: "Signatory of Invitation Letter", dataIndex: "signatoryInvitationLetter", key: "signatoryInvitationLetter" },
  { title: "Minute File Number", dataIndex: "signatoriesMinutes", key: "signatoriesMinutes" },
];

const MEETING_LINKS_COLUMNS = [
  { title: "Meeting", dataIndex: "meeting", key: "meeting" },
  { title: "Invitation Reference", dataIndex: "invitationReference", key: "invitationReference" },
  { title: "Invitation Letter", dataIndex: "invitationLetter", key: "invitationLetter" },
  { title: "WA/Email", dataIndex: "invitationSent", key: "invitationSent" },
  { title: "Minutes", dataIndex: "minutes", key: "minutes" },
  { title: "Attendance", dataIndex: "attendance", key: "attendance" },
  { title: "Allowance", dataIndex: "allowance", key: "allowance" },
];

function buildMeetingDetailsRows(meetings) {
  if (!meetings?.length) return [];
  return meetings.map((meeting) => ({
    key: meeting.key,
    meeting: meeting.meeting,
    invitationDate: meeting.invitationDate,
    meetingDate: meeting.meetingDate,
    interval: meeting.interval,
    signatoryInvitationLetter: meeting.signatoryInvitationLetter,
    signatoriesMinutes: meeting.signatoriesMinutes,
  }));
}

function buildMeetingLinkRows(meetings) {
  if (!meetings?.length) return [];

  return meetings.map((meeting) => ({
    key: meeting.key,
    meeting: meeting.meeting,
    invitationReference: meeting.invitationLetterReference || "N/A",
    invitationLetter: renderDpatEvidenceLink(
      meeting.invitationLetter ?? meeting.invitation,
      "View Letter",
    ),
    invitationSent: renderDpatEvidenceLink(meeting.invitationSent, "View WA/Email"),
    minutes: renderDpatEvidenceLink(meeting.docs, "View Minutes"),
    attendance: renderDpatEvidenceLink(meeting.attendance, "View Attendance"),
    allowance: renderDpatEvidenceLink(meeting.allowance, "View Allowance"),
  }));
}

const GAMeeting = forwardRef(({
  data,
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const displayData = resolveGaMeetingDataProp({
    isAssessmentStarted,
    savedAssessmentData,
    liveData: data,
  });

  const meetings = displayData?.meetings || [];
  const detailsRows = useMemo(() => buildMeetingDetailsRows(meetings), [meetings]);
  const linkRows = useMemo(() => buildMeetingLinkRows(meetings), [meetings]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI1",
      area: "General Assembly Meetings and Approvals",
      data: displayData,
    }),
  }));

  return (
    <Spin spinning={assessmentStartLoading} tip={assessmentStartLoading ? "Loading General Assembly Meetings data..." : undefined}>
      <div style={{ minHeight: assessmentStartLoading ? 120 : undefined }}>
        <Comment
          data={displayData}
          year={year}
          districtId={districtId}
          tableCommentedId={`c1.0-1.1-${year}`}
          hideComment={hideComment}
        >
          {({ renderCommentFeedbackFooter }) => (
            <DpatSubIndicatorSection code={GA_DPAT_SLUG}>
              <DpatIndicatorCmsIntro year={year} slug={GA_DPAT_SLUG} />

              <Col align="start">
                <CiResultDisplay slug={GA_DPAT_SLUG} fulfillment={displayData?.fulfillment} />
              </Col>

              <Title level={5} style={{ marginTop: "10px" }}>
                Number of Decisions: <strong>{displayData?.numberOfDecision ?? "N/A"}</strong>
              </Title>

              <Title level={4} style={{ marginTop: "20px" }}>
                Evidence of General Assembly Meetings of the Assembly
              </Title>
              <Table
                columns={MEETING_DETAILS_COLUMNS}
                dataSource={detailsRows}
                pagination={false}
                bordered
                rowKey="key"
              />

              <Title level={4} style={{ marginTop: "20px" }}>
                Meeting evidence links
              </Title>
              <Table
                columns={MEETING_LINKS_COLUMNS}
                dataSource={linkRows}
                pagination={false}
                bordered
                rowKey="key"
              />

              {!hideComment && renderCommentFeedbackFooter()}
            </DpatSubIndicatorSection>
          )}
        </Comment>
      </div>
    </Spin>
  );
});

export default GAMeeting;
